import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { atLeast, ApiError, CONFIG_KEYS, api, type Bench, type ConfigField } from "../api";

/**
 * Where a bench's addresses and capabilities live.
 *
 * The point of the phase: adding a rig becomes creating a record, not editing
 * `run.sh` on a machine you have to ssh into. `run.sh` reads these at the start
 * of every run.
 *
 * NO CREDENTIALS HERE, and the note below says so on the screen rather than only
 * in a commit message. The DUT password, the API keys and the UI passwords stay
 * on the bench: this service has ONE shared password, and putting secrets behind
 * it would make every bench token a way to read every rig's.
 */
/**
 * Capabilities as chips rather than a comma-separated string.
 *
 * A capability is a CLAIM ABOUT THE RIG - "this bench has a panel", "sudo works
 * here" - and scenarios are gated on them, so a typo does not fail loudly: the
 * scenario is silently skipped and the run looks clean. A free-text box makes
 * that typo easy and invisible. Chips make what is set visible at a glance, and
 * the known ones are offered rather than remembered.
 *
 * The stored value stays the comma-separated string run.sh already reads. This
 * is a better way to edit the same thing, not a new format for the bench to
 * learn.
 */
const KNOWN_CAPS = [
  "reversible", "destructive", "reboot", "reboot-persist", "rootshell",
  "panel", "speaker", "sivmock", "sivmon", "modem", "sim", "robustel",
  "wd-disarmed", "extscan", "netmutate", "credentials", "doorclosed",
  "dooropen", "cef", "mains", "kvm",
];

export function splitCaps(v: string): string[] {
  return (v || "")
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function CapsEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const caps = splitCaps(value);
  const set = (next: string[]) => onChange([...new Set(next)].join(","));

  return (
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap gap-1">
        {caps.map((c) => (
          <span
            key={c}
            className="inline-flex items-center gap-1 rounded bg-sky-100 px-1.5 py-0.5 font-mono text-xs text-sky-900 dark:bg-sky-950 dark:text-sky-200"
          >
            {c}
            <button
              className="text-sky-700 hover:text-sky-900 dark:text-sky-400"
              onClick={() => set(caps.filter((x) => x !== c))}
              aria-label={t("configPanel.capsRemove", { cap: c })}
            >
              ×
            </button>
          </span>
        ))}
        {caps.length === 0 && (
          <span className="text-xs italic text-slate-400">
            {t("configPanel.capsNone")}
          </span>
        )}
      </div>
      <div className="mt-1 flex gap-1">
        <input
          className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-1 font-mono text-xs dark:border-slate-700 dark:bg-slate-800"
          placeholder={t("configPanel.capsAdd")}
          value={draft}
          list="known-caps"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              const v = draft.trim();
              if (v) set([...caps, v]);
              setDraft("");
            }
          }}
        />
        <datalist id="known-caps">
          {KNOWN_CAPS.filter((c) => !caps.includes(c)).map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>
      </div>
      <div className="mt-1 flex flex-wrap gap-1">
        {KNOWN_CAPS.filter((c) => !caps.includes(c))
          .slice(0, 8)
          .map((c) => (
            <button
              key={c}
              className="rounded border border-slate-200 px-1.5 py-0.5 font-mono text-[11px] text-slate-500 hover:border-sky-300 hover:text-sky-700 dark:border-slate-700"
              onClick={() => set([...caps, c])}
            >
              + {c}
            </button>
          ))}
      </div>
    </div>
  );
}

export default function ConfigPanel({
  bench,
  schema,
  onSaved,
}: {
  bench: Bench;
  /** The fields THIS bench's project declares. SNCF asks for HIL_SSH; IPI6 is
      a different product on different hardware and asks for its own. */
  schema?: ConfigField[];
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  // The project's own schema wins, and its labels are what somebody typed into
  // the project - data, not UI, so they are shown as stored. Only the built-in
  // fallback list carries catalogue keys, and it is resolved here rather than
  // at module level so a language change re-renders it.
  const fields: ConfigField[] =
    schema && schema.length
      ? schema
      : CONFIG_KEYS.map(([key, help, example]) => ({
          key,
          label: t(help),
          example,
          kind: key === "HIL_CAPS" ? "caps" : "text",
        }));
  const initial: Record<string, string> = (() => {
    try {
      return JSON.parse(bench.config_json || "{}");
    } catch {
      return {};
    }
  })();
  const [vals, setVals] = useState<Record<string, string>>(initial);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = Object.entries(initial).filter(([, v]) => v);

  async function save() {
    setBusy(true);
    setError("");
    try {
      const clean: Record<string, string> = {};
      for (const [k, v] of Object.entries(vals)) if (v.trim()) clean[k] = v.trim();
      await api.updateBench(bench.id, { config: clean });
      setEditing(false);
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mt-6">
      <div className="flex items-center gap-3">
        <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">
          {t("configPanel.heading")}
        </h2>
        {/* Configuring a rig is the maintainer rung. A guest and an operator
            still READ this - what the bench is wired to is half of what a
            failed run means. */}
        {!editing && atLeast(bench.role, "maintainer") && (
          <button
            className="ml-auto rounded border border-slate-300 px-2 py-1 text-sm hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800"
            onClick={() => setEditing(true)}
          >
            {t("configPanel.edit")}
          </button>
        )}
      </div>

      {error && (
        <div className="mt-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
          {error}
        </div>
      )}

      <div className="mt-2 rounded-lg border border-slate-200 bg-white p-4 text-sm dark:border-slate-800 dark:bg-slate-900">
        {editing ? (
          <>
            <div className="space-y-3">
              {fields.map((f) => (
                <div key={f.key}>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="w-40 shrink-0 font-mono text-xs text-slate-500">
                      {f.key}
                    </label>
                    {f.kind === "caps" ? (
                      <CapsEditor
                        value={vals[f.key] ?? ""}
                        onChange={(v) => setVals({ ...vals, [f.key]: v })}
                      />
                    ) : (
                      <input
                        className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-1 font-mono text-xs dark:border-slate-700 dark:bg-slate-800"
                        placeholder={f.example}
                        value={vals[f.key] ?? ""}
                        onChange={(e) =>
                          setVals({ ...vals, [f.key]: e.target.value })
                        }
                      />
                    )}
                    <span className="w-full text-xs text-slate-400 sm:w-auto">
                      {f.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <button
                className="rounded bg-sky-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                disabled={busy}
                onClick={save}
              >
                {busy ? t("common.saving") : t("common.save")}
              </button>
              <button
                className="px-3 py-1.5 text-sm"
                onClick={() => {
                  setVals(initial);
                  setEditing(false);
                }}
              >
                {t("common.cancel")}
              </button>
            </div>
          </>
        ) : set.length === 0 ? (
          <p className="italic text-slate-500">
            <Trans i18nKey="configPanel.nothingSet" components={{ code: <code /> }} />
          </p>
        ) : (
          <dl className="grid max-w-2xl grid-cols-[10rem_1fr] gap-x-4 gap-y-1">
            {set.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="font-mono text-xs text-slate-500">{k}</dt>
                <dd className="break-all font-mono text-xs text-slate-700 dark:text-slate-300">
                  {v}
                </dd>
              </div>
            ))}
          </dl>
        )}
        <p className="mt-3 border-t border-slate-200 pt-2 text-xs text-slate-500 dark:border-slate-800">
          <Trans
            i18nKey="configPanel.note"
            components={{ code: <code />, b: <b /> }}
          />
        </p>
      </div>
    </section>
  );
}
